import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchICTData } from '../../../store/uiSlice';
import {
    fetchAllStudyModes,
    updateStudyModeStatus,
    insertStudyMode,
} from '../../../store/studyModeSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../Styles/Common.css';

const StudyModesTable = () => {
    const dispatch = useDispatch();
    const { studyModes, loading, error } = useSelector((state) => state.studyModes);
    const { ictData } = useSelector((state) => state.ui);

    const [searchTerm, setSearchTerm] = useState('');
    const [isInsertPopupOpen, setInsertPopupOpen] = useState(false);
    const [newStudyMode, setNewStudyMode] = useState({
        ictId_id: '',
        studyMode: '',
        studyModeDesc: '',
    });

    useEffect(() => {
        dispatch(fetchAllStudyModes());
        dispatch(fetchICTData());
    }, [dispatch]);

    const getICTName = (ictId) => {
        const ict = ictData.find((item) => item.id === ictId);
        return ict ? ict.ictName : 'Unknown';
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'A' ? 'D' : 'A';
        try {
            await dispatch(updateStudyModeStatus({ id, status: newStatus })).unwrap();
            toast.success(`Status updated to ${newStatus === 'A' ? 'Active' : 'Deactivated'}`);
        } catch {
            toast.error('Failed to update status');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewStudyMode({ ...newStudyMode, [name]: value });
    };

    const handleInsert = async (e) => {
        e.preventDefault();

        if (!newStudyMode.ictId_id || !newStudyMode.studyMode || !newStudyMode.studyModeDesc) {
            toast.error('Please fill in all required fields.');
            return;
        }

        const payload = {
            ...newStudyMode,
            status: 'A',
        };

        try {
            await dispatch(insertStudyMode(payload)).unwrap();
            toast.success('Study Mode inserted successfully!');
            setInsertPopupOpen(false);
            setNewStudyMode({
                ictId_id: '',
                studyMode: '',
                studyModeDesc: '',
            });
        } catch {
            toast.error('Failed to insert Study Mode.');
        }
    };

    const filteredStudyModes = studyModes.filter((mode) =>
        getICTName(mode.ictId_id).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="study-modes-container">
            <div className="header-container">
                <h2>Study Modes</h2>
                <div className="controls">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    <button
                        className="insert-btn"
                        onClick={() => setInsertPopupOpen(true)}
                    >
                        +
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading">Loading data...</div>
            ) : error ? (
                <div className="error">Error: {error}</div>
            ) : (
                <table className="study-modes-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>ICT ID</th>
                            <th>ICT Name</th>
                            <th>Study Mode</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredStudyModes.map((item, index) => (
                            <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>{item.ictId_id}</td>
                                <td>{getICTName(item.ictId_id)}</td>
                                <td>{item.studyMode}</td>
                                <td>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={item.status === 'A'}
                                            onChange={() => handleStatusToggle(item.id, item.status)}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {isInsertPopupOpen && (
                <div className="popup-overlay">
                    <div className="popup-content">
                        <h3>Insert New Study Mode</h3>
                        <form onSubmit={handleInsert} className="insert-form">
                            <div className="form-group">
                                <label>Select Course</label>
                                <select
                                    name="ictId_id"
                                    value={newStudyMode.ictId_id}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Course</option>
                                    {ictData.map((course) => (
                                        <option key={course.id} value={course.id}>
                                            {course.ictName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Study Mode</label>
                                <select
                                    name="studyMode"
                                    value={newStudyMode.studyMode}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Mode</option>
                                    <option value="Online">Online</option>
                                    <option value="Class Room">Class Room</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="studyModeDesc"
                                    rows="3"
                                    value={newStudyMode.studyModeDesc}
                                    onChange={handleInputChange}
                                    required
                                ></textarea>
                            </div>
                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setInsertPopupOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="submit-btn">
                                    Insert
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default StudyModesTable;
